import React, { useState } from 'react'
import Card from '../../../components/ui/Card'
import Button from '../../../components/ui/Button'
import Icon from '../../../components/AppIcon'

const PendingProducts = ({
  products,
  loading,
  onMarkActive,
  onPrintTag,
  selectedId,
  onSelect,
  onRetryPublish,
  onRetryInventory
}) => {
  const [activatingId, setActivatingId] = useState(null)
  const [retryingId, setRetryingId] = useState(null)
  const [stockProduct, setStockProduct] = useState(null)
  const [stockQuantity, setStockQuantity] = useState('')
  const [stockSaving, setStockSaving] = useState(false)
  const [stockError, setStockError] = useState('')

  const formatDate = (timestamp) => {
    if (!timestamp) return '-'
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const formatPrice = (price) => {
    if (!price && price !== 0) return '-'
    return `$${parseFloat(price).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
  }

  const handleMarkActive = async (productId) => {
    setActivatingId(productId)
    try {
      await onMarkActive(productId)
    } finally {
      setActivatingId(null)
    }
  }

  if (loading) {
    return (
      <Card variant="solid" className="border border-border/60 shadow-elevation-1">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="Clock" size={18} className="text-primary" />
          <h3 className="font-semibold text-foreground">Pending Products</h3>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse flex gap-4 items-center p-3 bg-muted/30 rounded-lg">
              <div className="w-14 h-14 bg-muted/60 rounded-lg" />
              <div className="flex-1">
                <div className="h-4 bg-muted/60 rounded w-1/3 mb-2" />
                <div className="h-3 bg-muted/60 rounded w-1/4" />
              </div>
            </div>
          ))}
        </div>
      </Card>
    )
  }

  if (!products || products.length === 0) {
    return (
      <Card variant="solid" className="border border-border/60 shadow-elevation-1">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="Clock" size={18} className="text-primary" />
          <h3 className="font-semibold text-foreground">Pending Products</h3>
        </div>
        <div className="text-center py-12 text-muted-foreground">
          <Icon name="CheckCircle" size={40} className="mx-auto mb-3 text-primary" />
          <p className="text-sm font-medium text-foreground">All products have photos!</p>
          <p className="text-xs mt-1">No products waiting for professional photos</p>
        </div>
      </Card>
    )
  }

  return (
    <Card variant="solid" className="border border-border/60 shadow-elevation-1">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon name="Clock" size={18} className="text-primary" />
          <h3 className="font-semibold text-foreground">Pending Products</h3>
          <span className="bg-primary/10 text-primary border border-primary/20 text-xs px-2 py-0.5 rounded-full">
            {products.length}
          </span>
        </div>
      </div>

      <p className="text-xs text-muted-foreground mb-4">
        Review Shopify publication status and products awaiting professional photos.
      </p>

      <div className="space-y-2">
        {products.map((product) => {
          const isActivating = activatingId === product.id

          return (
            <div
              key={product.id}
              onClick={() => onSelect?.(product)}
              className={`flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg transition-colors border cursor-pointer ${
                selectedId === product.id
                  ? 'bg-primary/10 border-primary/30'
                  : 'bg-muted/30 hover:bg-muted/50 border-border/60'
              }`}
            >
              {/* Thumbnail */}
              <div className="w-14 h-14 bg-background/80 rounded-lg overflow-hidden flex-shrink-0 border border-border/60">
                {product.initialImage ? (
                  <img
                    src={product.initialImage}
                    alt={product.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Icon name="Image" size={20} className="text-muted-foreground/60" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-foreground truncate">
                  {product.title || 'Untitled'}
                </p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="text-xs px-2 py-0.5 rounded-full inline-flex items-center bg-muted/50 text-foreground border border-border/60">
                    {product.inventorySyncStatus === 'failed' ? 'Stock not confirmed' : product.shopifyPublicationStage === 'pos' && !product.shopifyPublished ? 'POS publication pending' : product.shopifyError ? 'Shopify upload failed' : product.shopifyPublished ? 'Pending Photos' : 'Sending to Shopify'}
                  </span>
                  {product.sku && (
                    <span className="text-xs text-muted-foreground font-mono">{product.sku}</span>
                  )}
                  {product.category && (
                    <span className="text-xs text-muted-foreground capitalize">{product.category}</span>
                  )}
                </div>
              </div>

              {product.inventorySyncStatus === 'failed' ? <div role="alert" className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-black w-full sm:max-w-sm"><strong>Shopify stock was not confirmed</strong><p className="mt-1 break-words">{product.inventorySyncError || product.shopifyError}</p>{onRetryInventory && <Button variant="outline" size="sm" className="mt-2" onClick={e => { e.stopPropagation(); setStockQuantity(''); setStockError(''); setStockProduct(product) }}>Confirm current stock</Button>}</div> : product.shopifyError && <div role="alert" className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-black w-full sm:max-w-sm"><strong>Shopify publication needs attention</strong><p className="mt-1 break-words">{product.shopifyError}</p>{product.shopifyPublicationStage === 'pos' && !product.shopifyPublished && onRetryInventory && <Button variant="outline" size="sm" className="mt-2" onClick={e => { e.stopPropagation(); setStockQuantity(''); setStockError(''); setStockProduct(product) }}>Confirm stock and finish POS</Button>}{!product.shopifyId && !product.shopifyGid && <Button variant="outline" size="sm" className="mt-2" disabled={retryingId === product.id || product.shopifyPublishState === 'publishing'} onClick={async e => { e.stopPropagation(); setRetryingId(product.id); try { await onRetryPublish?.(product.id) } finally { setRetryingId(null) } }}>{retryingId === product.id ? 'Sending…' : 'Retry Shopify upload'}</Button>}</div>}
              {/* Price & Date */}
              <div className="hidden md:flex flex-col items-end gap-1 flex-shrink-0">
                <span className="text-sm font-semibold text-foreground">{formatPrice(product.salePrice)}</span>
                <span className="text-xs text-muted-foreground">{formatDate(product.createdAt)}</span>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation()
                    onPrintTag(product)
                  }}
                >
                  <Icon name="Printer" size={14} className="mr-1" />
                  Tag
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleMarkActive(product.id)
                  }}
                  disabled={isActivating || !product.shopifyPublished || !!product.shopifyError}
                  title="Mark as active (photos done)"
                >
                  <Icon name={isActivating ? "Loader2" : "Check"} size={14} className={isActivating ? 'animate-spin' : ''} />
                </Button>
              </div>
            </div>
          )
        })}
      </div>
      {stockProduct && <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => { if (!stockSaving) setStockProduct(null) }}><div role="dialog" aria-modal="true" aria-labelledby="stock-confirm-title" className="w-full max-w-md rounded-xl bg-white text-black p-5 space-y-4 shadow-xl" onClick={event => event.stopPropagation()}><h3 id="stock-confirm-title" className="font-semibold text-lg">Confirm current stock</h3><p className="text-sm">SKU {stockProduct.sku}. Enter the number of pieces currently in the ArtGold store, after sales and returns.</p><label className="block font-medium text-sm">Current pieces at ArtGold<input type="number" min="0" step="1" value={stockQuantity} onChange={event => setStockQuantity(event.target.value)} className="mt-1 w-full rounded-lg border border-gray-400 bg-white text-black px-3 py-2" /></label>{stockError && <p role="alert" className="text-sm text-red-800">{stockError}</p>}<div className="flex gap-3 justify-end"><Button variant="outline" disabled={stockSaving} onClick={() => setStockProduct(null)}>Cancel</Button><Button disabled={stockSaving || stockQuantity === ''} onClick={async () => { const qty = Number(stockQuantity); if (!Number.isSafeInteger(qty) || qty < 0) { setStockError('Enter a whole number of pieces, zero or more.'); return } setStockSaving(true); setStockError(''); try { await onRetryInventory(stockProduct.id, qty); setStockProduct(null) } catch(error) { setStockError(error.message || 'Shopify did not confirm the stock. Please try again.') } finally { setStockSaving(false) } }}>{stockSaving ? 'Confirming…' : 'Save current stock'}</Button></div></div></div>}
    </Card>
  )
}

export default PendingProducts
